// v10/src/components/admin/inventory/modals/PhotoUploadModal.js
//
// Modal "Foto del producto" del Stock de repuestos (A8) — Tarea 2.
// POST   /api/inventory-items/:id/photo  → { base64, mediaType }
// DELETE /api/inventory-items/:id/photo
//
// Solo imágenes (JPG, PNG, WebP) hasta 10MB. Patrón espejado del
// AttachmentModal de A6.1.

import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Loader, Upload, Image as ImageIcon, Trash2, RefreshCw, CheckCircle2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import inventoryService from '../../../../services/inventoryService';

const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 10 * 1024 * 1024;

const formatFileSize = (bytes, t) => {
  if (bytes < 1024 * 1024) {
    return t('inventory_modal_photo_size_kb', { kb: (bytes / 1024).toFixed(0) });
  }
  return t('inventory_modal_photo_size_mb', { mb: (bytes / (1024 * 1024)).toFixed(2) });
};

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = String(reader.result || '');
    const idx = result.indexOf('base64,');
    resolve(idx >= 0 ? result.slice(idx + 'base64,'.length) : result);
  };
  reader.onerror = () => reject(reader.error || new Error('FileReader error'));
  reader.readAsDataURL(file);
});

const PhotoUploadModal = ({ isOpen, item, onClose, onSuccess, darkMode }) => {
  const { t } = useTranslation('admin');

  const [photoUrl, setPhotoUrl] = useState(item?.photo_url || null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [validationError, setValidationError] = useState('');
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  const inputRef = useRef(null);
  const isMounted = useRef(true);
  useEffect(() => () => { isMounted.current = false; }, []);

  const busy = uploading || deleting;

  // Reset al abrir
  useEffect(() => {
    if (isOpen) {
      setPhotoUrl(item?.photo_url || null);
      setSelectedFile(null);
      setValidationError('');
      setError('');
      setConfirmDelete(false);
      setSuccessMessage(null);
    }
  }, [isOpen, item]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, busy]);

  useEffect(() => {
    if (!successMessage) return undefined;
    const timer = setTimeout(() => {
      if (isMounted.current) setSuccessMessage(null);
    }, 3000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  const validateFile = (file) => {
    if (!file) return t('inventory_modal_photo_error_type');
    if (!ALLOWED_MIME_TYPES.includes(file.type)) return t('inventory_modal_photo_error_type');
    if (file.size > MAX_BYTES) return t('inventory_modal_photo_error_size');
    return '';
  };

  const handleFilePicked = (file) => {
    setError('');
    if (!file) {
      setSelectedFile(null);
      setValidationError('');
      return;
    }
    const err = validateFile(file);
    if (err) {
      setSelectedFile(null);
      setValidationError(err);
      return;
    }
    setSelectedFile(file);
    setValidationError('');
  };

  const onPickClick = () => {
    if (busy) return;
    inputRef.current?.click();
  };

  const onFileInputChange = (e) => {
    const file = e.target.files?.[0] || null;
    handleFilePicked(file);
    if (e.target) e.target.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (busy) return;
    const file = e.dataTransfer?.files?.[0] || null;
    handleFilePicked(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || busy || !item?.id) return;
    const wasReplace = !!photoUrl;
    setUploading(true);
    setError('');
    try {
      const base64 = await fileToBase64(selectedFile);
      const res = await inventoryService.uploadPhoto(item.id, {
        base64,
        mediaType: selectedFile.type,
      });
      if (!isMounted.current) return;
      const data = res?.data?.data || {};
      setPhotoUrl(data.photo_url || null);
      setSelectedFile(null);
      setSuccessMessage(
        wasReplace
          ? t('inventory_modal_photo_success_replace')
          : t('inventory_modal_photo_success_upload'),
      );
      onSuccess?.();
    } catch (err) {
      if (!isMounted.current) return;
      setError(err?.response?.data?.message || t('inventory_modal_network_error'));
    } finally {
      if (isMounted.current) setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (busy || !item?.id) return;
    setDeleting(true);
    setError('');
    try {
      await inventoryService.deletePhoto(item.id);
      if (!isMounted.current) return;
      setPhotoUrl(null);
      setConfirmDelete(false);
      setSuccessMessage(t('inventory_modal_photo_success_delete'));
      onSuccess?.();
    } catch (err) {
      if (!isMounted.current) return;
      setError(err?.response?.data?.message || t('inventory_modal_network_error'));
    } finally {
      if (isMounted.current) setDeleting(false);
    }
  };

  if (!isOpen || !item) return null;

  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.45)' : '#64748b';
  const fgFaint   = darkMode ? 'rgba(255,255,255,0.30)' : '#94a3b8';

  const hasPhoto = !!photoUrl;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 110,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
      }}
      onClick={busy ? undefined : onClose}
    >
      <motion.div
        initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 20 }}
        style={{
          width: '100%', maxWidth: 600, borderRadius: 24,
          background: darkMode ? 'rgba(15,23,42,0.98)' : 'white',
          border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
          padding: 28, boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
          maxHeight: '90vh', overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12,
              background: 'linear-gradient(135deg,#0ea5e9,#0284c7)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 6px 16px rgba(14,165,233,0.3)',
            }}>
              <ImageIcon style={{ width: 20, height: 20, color: 'white' }} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: fgPrimary }}>
                {t('inventory_modal_photo_title')}
              </h3>
              <p style={{ margin: '3px 0 0', fontSize: 13, color: fgMuted }}>
                {t('inventory_modal_photo_subtitle', { name: item.name })}
              </p>
            </div>
          </div>
          <button
            type="button" onClick={onClose} disabled={busy}
            aria-label={t('inventory_modal_close')}
            style={{
              padding: 8, borderRadius: 10, border: 'none',
              background: 'transparent', cursor: busy ? 'not-allowed' : 'pointer',
              color: fgMuted,
            }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        <AnimatePresence>
          {successMessage && (
            <motion.div
              key="photo-success"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 14px', borderRadius: 12, marginBottom: 14,
                background: 'rgba(22,163,74,0.10)',
                border: '1px solid rgba(22,163,74,0.30)',
                color: '#16a34a',
              }}
            >
              <CheckCircle2 style={{ width: 16, height: 16, flexShrink: 0 }} />
              <span style={{ fontSize: 13, fontWeight: 700 }}>{successMessage}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Foto existente */}
        {hasPhoto && !confirmDelete && (
          <div>
            <div style={{
              borderRadius: 14, overflow: 'hidden', marginBottom: 14,
              border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
              background: darkMode ? 'rgba(255,255,255,0.02)' : '#f8fafc',
            }}>
              <img
                src={photoUrl}
                alt={item.name}
                style={{ width: '100%', maxHeight: 360, objectFit: 'contain', display: 'block' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <motion.button
                type="button" onClick={onPickClick} disabled={busy}
                whileHover={!busy ? { scale: 1.02 } : {}} whileTap={!busy ? { scale: 0.97 } : {}}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '10px 14px', borderRadius: 12, border: 'none',
                  cursor: busy ? 'not-allowed' : 'pointer',
                  background: 'linear-gradient(135deg,#4f46e5,#6366f1)',
                  color: 'white', fontWeight: 700, fontSize: 13, fontFamily: 'inherit',
                }}
              >
                <RefreshCw style={{ width: 14, height: 14 }} />
                {t('inventory_modal_photo_action_replace')}
              </motion.button>
              <motion.button
                type="button" onClick={() => setConfirmDelete(true)} disabled={busy}
                whileHover={!busy ? { scale: 1.02 } : {}} whileTap={!busy ? { scale: 0.97 } : {}}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '10px 14px', borderRadius: 12, border: 'none',
                  cursor: busy ? 'not-allowed' : 'pointer',
                  background: 'transparent',
                  color: '#ef4444', fontWeight: 700, fontSize: 13, fontFamily: 'inherit',
                }}
              >
                <Trash2 style={{ width: 14, height: 14 }} />
                {t('inventory_modal_photo_action_delete')}
              </motion.button>
            </div>
          </div>
        )}

        {/* Confirmación de delete */}
        {hasPhoto && confirmDelete && (
          <div style={{
            padding: 16, borderRadius: 14, marginBottom: 12,
            background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
          }}>
            <p style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#dc2626' }}>
              {t('inventory_modal_photo_confirm_delete')}
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <motion.button
                type="button" onClick={handleDelete} disabled={deleting}
                whileHover={!deleting ? { scale: 1.02 } : {}} whileTap={!deleting ? { scale: 0.97 } : {}}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', borderRadius: 10, border: 'none',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  background: 'linear-gradient(135deg,#dc2626,#ef4444)',
                  color: 'white', fontWeight: 700, fontSize: 13, fontFamily: 'inherit',
                }}
              >
                <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
                {deleting
                  ? <Loader style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />
                  : <Trash2 style={{ width: 14, height: 14 }} />
                }
                {deleting
                  ? t('inventory_modal_photo_action_deleting')
                  : t('inventory_modal_photo_action_delete')}
              </motion.button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                style={{
                  padding: '8px 14px', borderRadius: 10,
                  border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                  background: 'transparent',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  fontWeight: 600, fontSize: 13, color: fgMuted, fontFamily: 'inherit',
                }}
              >
                {t('inventory_modal_btn_cancel')}
              </button>
            </div>
          </div>
        )}

        {/* Drop zone (sin foto previa o tras eliminar) */}
        {!hasPhoto && (
          <div>
            <div
              role="button"
              tabIndex={0}
              onClick={onPickClick}
              onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !busy) onPickClick(); }}
              onDragOver={(e) => { e.preventDefault(); if (!busy) setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', gap: 10, padding: '40px 20px',
                borderRadius: 18, marginBottom: 14,
                border: dragOver
                  ? '2px dashed #6366f1'
                  : (darkMode ? '2px dashed rgba(255,255,255,0.12)' : '2px dashed #cbd5e1'),
                background: dragOver
                  ? (darkMode ? 'rgba(99,102,241,0.10)' : 'rgba(99,102,241,0.06)')
                  : (darkMode ? 'rgba(255,255,255,0.02)' : '#f8fafc'),
                cursor: busy ? 'not-allowed' : 'pointer',
                textAlign: 'center', outline: 'none',
                transition: 'background 0.15s, border-color 0.15s',
              }}
            >
              <div style={{
                width: 56, height: 56, borderRadius: 18,
                background: darkMode ? 'rgba(99,102,241,0.18)' : 'rgba(99,102,241,0.10)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Upload style={{ width: 26, height: 26, color: '#6366f1' }} />
              </div>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: fgPrimary }}>
                {t('inventory_modal_photo_drag_or_select')}
              </p>
              <p style={{ margin: 0, fontSize: 11, color: fgFaint }}>
                {t('inventory_modal_photo_allowed_types')}
              </p>
            </div>

            {selectedFile && (
              <div style={{
                padding: '10px 14px', borderRadius: 12, marginBottom: 12,
                background: darkMode ? 'rgba(255,255,255,0.04)' : '#f1f5f9',
                border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0',
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <ImageIcon style={{ width: 18, height: 18, color: '#0284c7' }} />
                <p style={{ margin: 0, fontSize: 12.5, color: fgPrimary, fontWeight: 600 }}>
                  {t('inventory_modal_photo_selected_file', {
                    name: selectedFile.name,
                    size: formatFileSize(selectedFile.size, t),
                  })}
                </p>
              </div>
            )}
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
          style={{ display: 'none' }}
          onChange={onFileInputChange}
        />

        {validationError && (
          <div style={{
            padding: '10px 14px', borderRadius: 12,
            background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
            marginBottom: 12,
          }}>
            <p style={{ margin: 0, fontSize: 12, color: '#ef4444', fontWeight: 600 }}>
              • {validationError}
            </p>
          </div>
        )}

        {error && (
          <div style={{
            padding: '10px 14px', borderRadius: 12,
            background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
            marginBottom: 12,
          }}>
            <p style={{ margin: 0, fontSize: 13, color: '#ef4444', fontWeight: 600 }}>{error}</p>
          </div>
        )}

        {selectedFile && !confirmDelete && (
          <div style={{ display: 'flex', gap: 10 }}>
            <motion.button
              type="button" onClick={handleUpload}
              disabled={uploading || !!validationError}
              whileHover={!uploading && !validationError ? { scale: 1.02 } : {}}
              whileTap={!uploading && !validationError ? { scale: 0.97 } : {}}
              style={{
                flex: 1, padding: 13, borderRadius: 14, border: 'none',
                cursor: uploading || validationError ? 'not-allowed' : 'pointer',
                background: uploading || validationError
                  ? (darkMode ? 'rgba(255,255,255,0.08)' : '#cbd5e1')
                  : 'linear-gradient(135deg,#0ea5e9,#0284c7)',
                color: 'white', fontWeight: 700, fontSize: 14, fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
              {uploading
                ? <Loader style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
                : <Upload style={{ width: 16, height: 16 }} />
              }
              {uploading
                ? t('inventory_modal_photo_action_uploading')
                : t('inventory_modal_photo_action_upload')}
            </motion.button>
            <button
              type="button"
              onClick={() => { setSelectedFile(null); setValidationError(''); }}
              disabled={uploading}
              style={{
                padding: '13px 18px', borderRadius: 14,
                border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                background: 'transparent',
                cursor: uploading ? 'not-allowed' : 'pointer',
                fontWeight: 600, fontSize: 14, color: fgMuted, fontFamily: 'inherit',
              }}
            >
              {t('inventory_modal_btn_cancel')}
            </button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default PhotoUploadModal;
